import Button from 'components/ui/Button';
import CollapsibleSection from 'components/ui/CollapsibleSection';
import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { b2cService } from 'services/b2cService';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { userSelector } from 'store/reducers/usersSlice';
import { styled } from 'styled-components';

const EXAMPLE = `{
  "receivers": [
    {
      "name": "Tenson",
      "enabled": true,
      "orderTypes": ["B2C"],
      "externalSystem": "Centra",
      "queue": {
        "type": "ServiceBus",
        "connectionSettingName": "B2CReturns:Tenson",
        "queueName": "rc-returns"
      }
    }
  ]
}`;

/**
 * The selected company's B2B2C queue config. Admins only: it decides which returns are sent to
 * which e-commerce queue. Holds app setting names, never connection strings.
 */
const B2CQueueSection = () => {
    const { t } = useTranslation();
    const isAdmin = useAppSelector(userSelector.selectIsAdmin);
    const currentCompany = useAppSelector(companiesSelector.selectCurrentCompany);

    const [configJson, setConfigJson] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);

    useEffect(() => {
        if (!isAdmin) return;

        setIsLoading(true);
        setErrors([]);
        setWarnings([]);
        b2cService
            .getConfig()
            .then((json) => setConfigJson(json ?? ''))
            .catch(() => toast.error(t('b2c.admin.loadFailed')))
            .finally(() => setIsLoading(false));
    }, [isAdmin, currentCompany?.id, t]);

    if (!isAdmin) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const result = await b2cService.saveConfig(configJson);
            setErrors(result.errors ?? []);
            setWarnings(result.warnings ?? []);
            if (result.saved) {
                if (result.configJson) setConfigJson(result.configJson);
                toast.success(t('b2c.admin.saved'), { autoClose: 3500, closeOnClick: true });
            }
        } catch {
            toast.error(t('b2c.admin.saveFailed'), { autoClose: 3500, closeOnClick: true });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Wrapper column gap="0">
            <CollapsibleSection openLabel={t('b2c.admin.title')}>
                <Content>
                    <Text className="f3-400 mb-0" fontSize={12} color="textFaded">
                        {t('b2c.admin.info', { company: currentCompany?.name ?? '' })}
                    </Text>
                    {isLoading ? (
                        <Spinner size="medium" center useFullHeight={false} />
                    ) : (
                        <ConfigInput
                            data-allow-paste
                            value={configJson}
                            placeholder={EXAMPLE}
                            spellCheck={false}
                            onChange={(e) => setConfigJson(e.target.value)}
                        />
                    )}
                    {errors.map((error) => (
                        <Text key={error} color="error" fontSize={12} margin="0">
                            {error}
                        </Text>
                    ))}
                    {warnings.map((warning) => (
                        <Text key={warning} color="textFaded" fontSize={12} margin="0">
                            {warning}
                        </Text>
                    ))}
                    <Flex gap="0.5rem">
                        <Button onClick={handleSave} disabled={isSaving || isLoading} border rounded>
                            {t('save')}
                        </Button>
                        {!configJson && (
                            <Button onClick={() => setConfigJson(EXAMPLE)} border rounded>
                                {t('b2c.admin.insertExample')}
                            </Button>
                        )}
                    </Flex>
                </Content>
            </CollapsibleSection>
        </Wrapper>
    );
};

export default B2CQueueSection;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 1rem;
`;

const ConfigInput = styled.textarea`
    font-family: monospace;
    font-size: 13px;
    min-height: 320px;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: var(--radius);
    resize: vertical;
`;
